/**
 * PR-26 — Haptic & Sound Engagement.
 * Configurable vibration + audio cues per event category. Uses the Web
 * Vibration API + WebAudio synthesized beeps so no asset bundle is required.
 */
import { trackEvent } from "@/lib/audit";

export type HapticCategory =
  | "goal"
  | "shot_on_goal"
  | "penalty"
  | "card"
  | "poll"
  | "question"
  | "coupon"
  | "reward"
  | "breaking_news"
  | "important_push";

export type HapticIntensity = "low" | "medium" | "high";

export interface HapticSoundSettings {
  enabled: Record<HapticCategory, boolean>;
  intensity: HapticIntensity;
  silent_mode: boolean;
  sound_enabled: boolean;
  vibration_enabled: boolean;
}

const KEY = "vs:pr26:haptics";

const DEFAULTS: HapticSoundSettings = {
  enabled: {
    goal: true, shot_on_goal: true, penalty: true, card: false,
    poll: false, question: false, coupon: true, reward: true,
    breaking_news: true, important_push: true,
  },
  intensity: "medium",
  silent_mode: false,
  sound_enabled: true,
  vibration_enabled: true,
};

export function getHapticSettings(): HapticSoundSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch { return DEFAULTS; }
}

export function updateHapticSettings(patch: Partial<HapticSoundSettings>): HapticSoundSettings {
  const next = { ...getHapticSettings(), ...patch };
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

const PATTERNS: Record<HapticIntensity, number[]> = {
  low: [25],
  medium: [60, 40, 60],
  high: [120, 60, 120, 60, 200],
};

const SOUND_FREQ: Record<HapticCategory, number> = {
  goal: 880, shot_on_goal: 660, penalty: 520, card: 320,
  poll: 740, question: 700, coupon: 900, reward: 980,
  breaking_news: 600, important_push: 540,
};

export function triggerHaptic(category: HapticCategory): boolean {
  const s = getHapticSettings();
  if (s.silent_mode || !s.vibration_enabled || !s.enabled[category]) return false;
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return false;
  navigator.vibrate(PATTERNS[s.intensity]);
  void trackEvent("haptic_triggered", { category, intensity: s.intensity });
  return true;
}

export function triggerSound(category: HapticCategory): boolean {
  const s = getHapticSettings();
  if (s.silent_mode || !s.sound_enabled || !s.enabled[category]) return false;
  if (typeof window === "undefined") return false;
  const Ctor: typeof AudioContext | undefined =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return false;
  try {
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = SOUND_FREQ[category];
    osc.type = "sine";
    gain.gain.value = s.intensity === "high" ? 0.18 : s.intensity === "medium" ? 0.1 : 0.05;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
    void trackEvent("sound_triggered", { category, freq: SOUND_FREQ[category] });
    return true;
  } catch { return false; }
}

export function triggerEngagement(category: HapticCategory): void {
  triggerHaptic(category);
  triggerSound(category);
}